import useDocumentMeta from '../hooks/useDocumentMeta.js';

export default function TermsOfService() {
  useDocumentMeta({
    title: 'Terms of Service | Account Editor',
    description:
      'Read the terms and conditions governing your use of the Account Editor Shopify app and website, including the applicable terms of service.',
    path: '/terms'
  });

  return (
    <section className="terms">
        <div className="container">
          <header className="terms__hero">
            <span className="eyebrow">
              Legal
            </span>
            <h1 className="terms__title">Terms of service</h1>
            <p className="terms__lede">
              Protecting your data: our commitment to privacy and security practices.
            </p>
            <span className="terms__updated">Last updated: May 14, 2026</span>
          </header>

          <article className="terms__article">
            <p className="terms__intro">
              Welcome to Account Editor! These Terms of Service (“Terms”) govern your use of the
              Account Editor application (“App”) and the related services (“Services”). By
              installing, accessing, or using Account Editor, you agree to be bound by these Terms,
              along with our Privacy Policy, Cookie Policy, and any other policies referenced
              herein. If you do not agree with any part of these Terms, please discontinue use of
              the Services immediately.
            </p>

            <p className="terms__p">
              Please take the time to read these Terms carefully before using the Services. This
              agreement is between you (the "Merchant" or "User") and Account Editor (referred to
              as "we," "our," or "the Company").
            </p>

            <section className="terms__section">
              <h2 className="terms__h2">
                <span className="terms__h2-num">01</span>
                Agreement to terms
              </h2>
              <p className="terms__p">
                By installing and using the Account Editor app, you affirm that:
              </p>
              <ul className="terms__list">
                <li>You possess the legal authority to enter into this agreement either individually or on behalf of an organization.</li>
                <li>You are at least 18 years old or the legal age of majority in your jurisdiction.</li>
                <li>You have read, understood, and agree to comply with these Terms of Service, the Privacy Policy, and all applicable laws and regulations.</li>
              </ul>
              <p className="terms__p">
                In addition, you are responsible for ensuring that your employees, agents, or any
                third parties who access the App on your behalf understand and comply with these
                Terms.
              </p>
            </section>

            <section className="terms__section">
              <h2 className="terms__h2">
                <span className="terms__h2-num">02</span>
                Modifications to terms
              </h2>
              <p className="terms__p">
                We reserve the right to update or modify these Terms at any time. Any changes to
                these Terms will take effect immediately upon being posted within the App or on our
                website. It is your responsibility to periodically review these Terms for any
                updates. Continued use of the Services after the changes are posted constitutes
                your acceptance of the revised Terms.
              </p>
            </section>

            <section className="terms__section">
              <h2 className="terms__h2">
                <span className="terms__h2-num">03</span>
                Services overview
              </h2>
              <p className="terms__p">
                Account Editor provides an advanced set of features aimed at improving your
                customers' post-purchase experience. These features include the ability to edit
                orders, manage cancellations, and handle notifications. The following outlines the
                core functionality:
              </p>

              <h3 className="terms__h3">3.1 Editable components</h3>
              <p className="terms__p">
                <strong>Customizable Order Edits:</strong> Account Editor allows merchants to
                enable or disable the option for customers to edit their orders. Customers can
                modify critical information such as:
              </p>
              <ul className="terms__list">
                <li>Contact details</li>
                <li>Shipping address</li>
                <li>Product details (e.g., size, color, quantity)</li>
              </ul>
              <p className="terms__p">
                <strong>Control Over Editable Components:</strong> Merchants can determine which
                components of an order can be edited and under what conditions. For example, you
                may choose to allow customers to update their shipping information but restrict
                product modifications.
              </p>

              <h3 className="terms__h3">3.2 Order modification timeframes</h3>
              <p className="terms__p">
                <strong>Customizable Time Windows:</strong> Merchants can configure specific
                timeframes within which customers can edit their orders. These windows are
                customizable to fit the nature of the products you sell. For instance:
              </p>
              <ul className="terms__list">
                <li>Default timeframe: 24 hours</li>
                <li>Custom timeframes can be set based on business needs</li>
              </ul>
              <p className="terms__p">
                <strong>Timeframe Enforcement:</strong> After the defined modification window
                expires, customers will no longer be able to make changes to their orders, and they
                may be required to contact support for further assistance.
              </p>

              <h3 className="terms__h3">3.3 Smart cancellations</h3>
              <p className="terms__p">
                <strong>Flexible Refund Options:</strong> Account Editor allows merchants to
                configure flexible cancellation policies, offering customers the option to cancel
                orders and receive a refund. Merchants can offer refunds in the form of:
              </p>
              <ul className="terms__list">
                <li><strong>Store Credit:</strong> A percentage of the purchase or full order value can be refunded as store credit, providing the customer with the option to make future purchases.</li>
                <li><strong>Direct Payments:</strong> Refunds can be issued directly to the customer's original payment method.</li>
                <li><strong>Restocking Fees:</strong> Merchants can set configurable restocking fees for canceled orders, ensuring that the business is protected from costs incurred by returns.</li>
              </ul>

              <h3 className="terms__h3">3.4 Audit trails</h3>
              <p className="terms__p">
                <strong>Order Modification Logs:</strong> Every change made to an order, whether it
                is an update to the shipping details or a cancellation, is logged in an Audit
                Trail. This log maintains a record of who made the change, when it was made, and
                what specific modifications were applied.
              </p>
              <p className="terms__p">
                <strong>Transparency and Accountability:</strong> The audit trail helps merchants
                track order modifications for transparency and ensures compliance with business
                policies and regulatory requirements.
              </p>

              <h3 className="terms__h3">3.5 Notifications</h3>
              <p className="terms__p">
                <strong>Real-Time Alerts:</strong> Both merchants and customers will receive
                notifications whenever changes are made to an order. Notifications may include:
              </p>
              <ul className="terms__list">
                <li>Confirmation of order modifications</li>
                <li>Cancellations and refund processing</li>
                <li>Shipping status updates</li>
              </ul>
              <p className="terms__p">
                <strong>Customizable Notification Settings:</strong> Merchants can control what
                type of notifications are sent to customers, allowing for a tailored communication
                experience.
              </p>

              <h3 className="terms__h3">3.6 API access and integration</h3>
              <p className="terms__p">
                Account Editor provides an Application Programming Interface (API) that allows
                developers and merchants to interact with the Service programmatically. The
                GraphQL API enables seamless integration with third-party applications, offering
                specific functionality for order editing, customer details, and other related
                features.
              </p>
              <p className="terms__p">The GraphQL API allows Users to:</p>
              <ul className="terms__list">
                <li><strong>Fetch Specific Data:</strong> Retrieve only the necessary data with minimal over-fetching or under-fetching, optimizing performance.</li>
                <li><strong>Perform Real-Time Updates:</strong> Modify editable components, including order items, shipping details, and customer information in real time.</li>
                <li><strong>Enhance Integration:</strong> Integrate Account Editor’s functionality into your existing systems or workflows, improving operational efficiency and flexibility.</li>
                <li><strong>Optimize Requests:</strong> Execute multiple data operations within a single query, enhancing request efficiency.</li>
                <li><strong>Scalability and Flexibility:</strong> Designed to be scalable and flexible, allowing customization and growth for merchants of all sizes while adhering to industry best practices and data security standards.</li>
              </ul>
              <p className="terms__p">
                By using the GraphQL API, merchants and developers can take full advantage of the
                powerful features offered by Account Editor, integrating them into their broader
                e-commerce systems for maximum efficiency and control.
              </p>
            </section>

            <section className="terms__section">
              <h2 className="terms__h2">
                <span className="terms__h2-num">04</span>
                Account setup
              </h2>
              <p className="terms__p">To use the Services, you must:</p>
              <ul className="terms__list">
                <li>Install the Account Editor app from the Shopify App Store.</li>
                <li>Grant the necessary permissions to the app, such as access to order details, customer information, and the ability to edit or modify orders.</li>
                <li>Configure the app’s settings to align with your store’s policies, including defining editable components, modifying timeframes, and setting cancellation rules.</li>
              </ul>
              <p className="terms__p">
                By configuring the app, you acknowledge that you are responsible for ensuring the
                accuracy of the settings and for implementing the app in a manner consistent with
                your store’s policies.
              </p>
            </section>

            <section className="terms__section">
              <h2 className="terms__h2">
                <span className="terms__h2-num">05</span>
                Payment and pricing
              </h2>

              <h3 className="terms__h3">5.1 Subscription plans</h3>
              <p className="terms__p">
                Account Editor offers both free and paid subscription plans. Each plan provides
                different features and functionalities, which are outlined in the Shopify App
                Store. You can choose a plan based on the features you require for your store.
              </p>
              <ul className="terms__list">
                <li><strong>Free Plan:</strong> Includes basic features with limited functionality.</li>
                <li><strong>Paid Plans:</strong> Include premium features such as advanced customization, extended timeframes, and enhanced support.</li>
              </ul>

              <h3 className="terms__h3">5.2 Billing and payment</h3>
              <ul className="terms__list">
                <li><strong>Billing:</strong> All subscription fees are billed through Shopify according to your selected plan. Charges will be processed via Shopify’s payment system, and you authorize Shopify to charge your payment method for subscription renewals.</li>
                <li><strong>Recurring Charges:</strong> Payments are typically billed on a monthly or annual basis, depending on the plan you choose. Recurring payments will continue unless you decide to cancel the subscription.</li>
                <li><strong>Free Trials:</strong> If a free trial is offered, it is limited to the specified duration and will automatically convert to a paid plan once the trial period ends unless canceled.</li>
              </ul>

              <h3 className="terms__h3">5.3 Refunds</h3>
              <p className="terms__p">
                <strong>Non-Refundable Payments:</strong> All subscription payments are
                non-refundable unless otherwise required by applicable law. If you cancel your
                subscription, you will not be entitled to a refund for any payments made during the
                current billing cycle.
              </p>
            </section>

            <section className="terms__section">
              <h2 className="terms__h2">
                <span className="terms__h2-num">06</span>
                User responsibilities
              </h2>
              <p className="terms__p">By using the Services, you agree to:</p>
              <ul className="terms__list">
                <li><strong>Accurate Configuration:</strong> Ensure that the app is configured responsibly and accurately to prevent misuse or errors in order modifications.</li>
                <li><strong>Compliance:</strong> You will comply with all applicable laws, including data protection regulations, when using the Services.</li>
                <li><strong>Data Integrity:</strong> Maintain the accuracy and integrity of the data entered into the system, such as customer contact information and order details.</li>
                <li><strong>Confidentiality:</strong> Safeguard the confidentiality of sensitive customer data in compliance with applicable privacy laws, including the GDPR (if relevant).</li>
              </ul>
            </section>

            <section className="terms__section">
              <h2 className="terms__h2">
                <span className="terms__h2-num">07</span>
                Data privacy and security
              </h2>
              <p className="terms__p">
                Account Editor collects and processes certain data to provide the Services. This
                data includes:
              </p>
              <ul className="terms__list">
                <li><strong>Order and Customer Data:</strong> We collect information related to customer orders, including product details, shipping information, and payment details to facilitate order modifications.</li>
                <li><strong>Audit Logs:</strong> We maintain logs of all order modifications, which can include sensitive customer and transaction information, to ensure transparency and accountability.</li>
                <li><strong>Notification Data:</strong> Information is also collected when notifications are sent to merchants or customers about order modifications.</li>
              </ul>
              <p className="terms__p">
                We are committed to protecting your data in compliance with applicable privacy
                regulations. Please refer to our Privacy Policy for more information on how we
                handle your data.
              </p>
            </section>

            <section className="terms__section">
              <h2 className="terms__h2">
                <span className="terms__h2-num">08</span>
                Intellectual property
              </h2>
              <p className="terms__p">
                All content, features, and functionalities of the App, including but not limited to
                the software, design, branding, logos, and documentation, are the intellectual
                property of Account Editor or its licensors. As a user, you may not copy, modify,
                distribute, or reverse-engineer any part of the App without prior written consent
                from Account Editor.
              </p>
            </section>

            <section className="terms__section">
              <h2 className="terms__h2">
                <span className="terms__h2-num">09</span>
                Limitations of liability
              </h2>
              <p className="terms__p">
                To the fullest extent permitted by law, Account Editor is provided "as is" and "as
                available" without warranties of any kind. We do not guarantee that the App will be
                error-free, uninterrupted, or secure. We are not liable for any indirect,
                incidental, or consequential damages arising from the use of the App.
              </p>
              <p className="terms__p">
                Our total liability to you is limited to the fees paid by you in the 12 months
                preceding the claim.
              </p>
            </section>

            <section className="terms__section">
              <h2 className="terms__h2">
                <span className="terms__h2-num">10</span>
                Termination
              </h2>

              <h3 className="terms__h3">10.1 By the merchant</h3>
              <p className="terms__p">
                You may uninstall the App at any time via your Shopify Admin panel. Upon
                uninstallation, you will lose access to all features, including any stored data or
                logs related to your account.
              </p>

              <h3 className="terms__h3">10.2 By Account Editor</h3>
              <p className="terms__p">
                We reserve the right to terminate or suspend your access to the Services if you
                violate any of these Terms or engage in unlawful activities.
              </p>
            </section>

            <section className="terms__section">
              <h2 className="terms__h2">
                <span className="terms__h2-num">11</span>
                Third-party services
              </h2>
              <p className="terms__p">
                Account Editor integrates with third-party platforms like Shopify. By using such
                integrations, you agree to comply with the terms and conditions of these
                third-party services.
              </p>
            </section>

            <section className="terms__section">
              <h2 className="terms__h2">
                <span className="terms__h2-num">12</span>
                Prohibited activities
              </h2>
              <p className="terms__p">You agree not to:</p>
              <ul className="terms__list">
                <li>Engage in unlawful or fraudulent activities using the App.</li>
                <li>Attempt to reverse-engineer or disrupt the functionality of the App.</li>
                <li>Misuse customer data obtained through the App.</li>
              </ul>
            </section>

            <section className="terms__section">
              <h2 className="terms__h2">
                <span className="terms__h2-num">13</span>
                Force majeure
              </h2>
              <p className="terms__p">
                We are not liable for delays or failures in performance resulting from causes
                beyond our reasonable control, such as natural disasters, acts of war, or technical
                failures.
              </p>
            </section>

            <section className="terms__section">
              <h2 className="terms__h2">
                <span className="terms__h2-num">14</span>
                Governing law
              </h2>
              <p className="terms__p">
                These Terms are governed by the laws of [Your Jurisdiction]. Any disputes related
                to these Terms will be resolved exclusively in the courts of [Your Jurisdiction].
              </p>
            </section>

            <section className="terms__section">
              <h2 className="terms__h2">
                <span className="terms__h2-num">15</span>
                Support
              </h2>
              <p className="terms__p">For assistance, please contact us via:</p>
              <div className="terms__contact">
                <span className="terms__contact-label">Email</span>
                <a href="mailto:support@accounteditor.com">
                  <i className="ph ph-envelope-simple" aria-hidden="true"></i>
                  support@accounteditor.com
                </a>
              </div>
            </section>

            <section className="terms__section">
              <h2 className="terms__h2">
                <span className="terms__h2-num">16</span>
                Entire agreement
              </h2>
              <p className="terms__p">
                These Terms, along with the Privacy Policy, Cookie Policy, and any other referenced
                documents, constitute the entire agreement between you and Account Editor. If any
                provision of these Terms is found to be unenforceable, the remaining provisions
                will remain in effect.
              </p>
            </section>
          </article>
        </div>
      </section>
  );
}
